import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Redirect, useHistory } from 'react-router-dom';
import { canModifyFestivals } from '@/lib/account-roles';
import {
  buildFestivalLocationPayload,
  isLocationNameSet,
  LOCATION_NAME_REQUIRED_MESSAGE,
} from '@/lib/festival-location';
import { hasLocationCoordinates } from '@/lib/google-maps';
import { INVALID_DATE_MESSAGE, isInvalidCompleteDate } from '@/lib/date-mask';
import { scrollToFirstFormError } from '@/lib/scroll-to-form-error';
import { withServerDateFieldsList } from '@/lib/server-date';
import { useAuthStore } from '@/stores/auth-store';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import {
  useChangeFestivalStatusMutation,
  useSaveFestivalMutation,
} from '../../queries/festivals/mutations';
import { useFestivalDetailQuery } from '../../queries/useFestivalDetailQuery';
import {
  Button,
  DateFormWidget,
  FestivalHeader,
  FileUpload,
  FormRow,
  Loading,
  LocationMapField,
} from '../controls';
import { FestivalEventStatusPanel } from './FestivalEventStatusPanel';
import { FestivalQrCodesSidebar } from './FestivalPromotionImages';

const defaultDateObject = {
  startTime: null,
  endTime: null,
  startTimeMS: null,
  endTimeMS: null,
  selectedDate: null,
  dateInput: null,
};

const defaultLinks = {
  instagram: '',
  twitter: '',
  facebook: '',
  web: '',
};

function getDatesValidationError(dates) {
  if (!dates?.length) {
    return 'At least one date is required.';
  }

  const invalidDateRow = dates.find((dateObj) =>
    isInvalidCompleteDate(dateObj.dateInput),
  );
  if (invalidDateRow) {
    return INVALID_DATE_MESSAGE;
  }

  const filledRows = dates.filter(
    (dateObj) =>
      dateObj.startTimeMS != null ||
      dateObj.endTimeMS != null ||
      Boolean(dateObj.selectedDate) ||
      Boolean(dateObj.dateInput),
  );
  if (filledRows.length === 0) {
    return 'At least one date is required.';
  }

  const incompleteRow = filledRows.find(
    (dateObj) => !dateObj.startTimeMS || !dateObj.endTimeMS,
  );
  if (incompleteRow) {
    return 'Each date must include a date, start time, and end time.';
  }

  return null;
}

function getCompleteDates(dates) {
  return (dates ?? []).filter(
    (dateObj) => dateObj.startTimeMS != null && dateObj.endTimeMS != null,
  );
}

function locationIsSet(location) {
  return hasLocationCoordinates(location);
}

function computeWorkspaceErrors(festival, locationName) {
  const nextWorkspaceErrors = {};
  const datesError = getDatesValidationError(festival.dates);
  if (datesError) {
    nextWorkspaceErrors.dates = datesError;
  }
  if (!locationIsSet(festival.location)) {
    nextWorkspaceErrors.location =
      'Location is required. Search for a place or click the map to set coordinates.';
  }
  if (!isLocationNameSet(locationName)) {
    nextWorkspaceErrors.locationName = LOCATION_NAME_REQUIRED_MESSAGE;
  }
  return nextWorkspaceErrors;
}

function buildFormValues(festival) {
  return {
    name: festival.name || '',
    description: festival.description || '',
    locationName: festival.location?.locationName ?? '',
    links: { ...defaultLinks, ...(festival.links || {}) },
  };
}

function FestivalFormFields({
  festival,
  patchFestival,
  saveMutation,
  statusMutation,
  loading,
  bridgeLoading,
  readOnly = false,
  onNewFestivalSaved,
}) {
  const history = useHistory();
  const [workspaceErrors, setWorkspaceErrors] = useState({});
  const formRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: buildFormValues(festival),
    mode: 'onSubmit',
  });

  const watchedName = watch('name');
  const watchedLocationName = watch('locationName');

  useEffect(() => {
    reset(buildFormValues(festival));
  }, [festival.festivalId, reset]);

  const getScrollRoot = useCallback(() => {
    return (
      formRef.current?.closest('[data-modal-scroll]') ??
      formRef.current ??
      document
    );
  }, []);

  const scrollToFirstError = useCallback(
    (rhfErrors = {}, workspace = workspaceErrors) => {
      scrollToFirstFormError(getScrollRoot(), [
        { hasError: Boolean(rhfErrors.name), selector: '[name="name"]' },
        {
          hasError: Boolean(workspace.dates),
          selector: '[data-form-field="dates"]',
        },
        {
          hasError: Boolean(workspace.location || workspace.locationName),
          selector: '[data-form-field="location"]',
        },
        {
          hasError: Boolean(rhfErrors.description),
          selector: '[name="description"]',
        },
      ]);
    },
    [getScrollRoot, workspaceErrors],
  );

  const handleDateChange = useCallback(
    (dates) => {
      patchFestival((current) => ({ ...current, dates }));
      setWorkspaceErrors((prev) => {
        const next = { ...prev };
        delete next.dates;
        return next;
      });
    },
    [patchFestival],
  );

  const handleLocationChange = useCallback(
    (locationUpdate, displayName) => {
      patchFestival((current) => ({
        location: {
          ...(current.location ?? {}),
          ...locationUpdate,
          ...(displayName != null ? { locationName: displayName } : null),
          radius: locationUpdate.radius ?? current.location?.radius ?? 1000,
        },
      }));
      if (displayName != null) {
        setValue('locationName', displayName, { shouldDirty: true });
      }
      setWorkspaceErrors((prev) => {
        const next = { ...prev };
        delete next.location;
        if (isLocationNameSet(displayName)) {
          delete next.locationName;
        }
        return next;
      });
    },
    [patchFestival, setValue],
  );

  const handleLocationNameChange = useCallback(
    (nextLocationName) => {
      setValue('locationName', nextLocationName, { shouldDirty: true });
      patchFestival((current) => ({
        location: {
          ...(current.location ?? {}),
          locationName: nextLocationName,
        },
      }));
      if (isLocationNameSet(nextLocationName)) {
        setWorkspaceErrors((prev) => {
          const next = { ...prev };
          delete next.locationName;
          return next;
        });
      }
    },
    [patchFestival, setValue],
  );

  const onSubmit = async (values) => {
    const nextWorkspaceErrors = computeWorkspaceErrors(
      festival,
      values.locationName,
    );
    if (Object.keys(nextWorkspaceErrors).length > 0) {
      setWorkspaceErrors(nextWorkspaceErrors);
      requestAnimationFrame(() => {
        scrollToFirstError({}, nextWorkspaceErrors);
      });
      return;
    }
    setWorkspaceErrors({});

    const updatedFestival = {
      ...festival,
      name: values.name.trim(),
      description: values.description.trim(),
      links: values.links,
      dates: withServerDateFieldsList(getCompleteDates(festival.dates)),
      location: buildFestivalLocationPayload(
        festival.location,
        values.locationName,
        festival.orgId,
      ),
    };

    delete updatedFestival.startDate;

    const wasNewFestival = !festival.festivalId;
    const saved = await saveMutation.mutateAsync(updatedFestival);

    if (wasNewFestival && saved?.festivalId) {
      onNewFestivalSaved?.(saved);
      history.push(`/festival/${saved.festivalId}/view`);
    }
  };

  const onFileUpload = useCallback(
    (fileInfo) => {
      const { filename, url } = fileInfo;
      patchFestival((current) => ({
        ...current,
        photoURL: url,
        photo: filename,
      }));
    },
    [patchFestival],
  );

  const onFileDelete = useCallback(() => {
    patchFestival((current) => ({
      ...current,
      photoURL: null,
      photo: null,
    }));
  }, [patchFestival]);

  const handleStatusChange = async (status) => {
    const current = festival.status || 'UNPUBLISHED';
    if (!festival.festivalId || status === current) {
      return;
    }
    patchFestival({ ...festival, status });
    try {
      await statusMutation.mutateAsync({
        festivalId: festival.festivalId,
        status,
      });
    } catch {
      patchFestival({ ...festival, status: current });
    }
  };

  const onInvalid = useCallback(
    (rhfErrors) => {
      const nextWorkspaceErrors = computeWorkspaceErrors(
        festival,
        getValues('locationName'),
      );
      setWorkspaceErrors(nextWorkspaceErrors);
      requestAnimationFrame(() => {
        scrollToFirstError(rhfErrors, nextWorkspaceErrors);
      });
    },
    [festival, getValues, scrollToFirstError],
  );

  const submitForm = handleSubmit(onSubmit, onInvalid);
  const showLoading = bridgeLoading || loading || isSubmitting;
  const dates =
    festival.dates?.length > 0 ? festival.dates : [defaultDateObject];
  const headerTitle =
    festival?.name?.trim() || watchedName?.trim() || 'Add New Event';
  const useSidebarLayout = Boolean(festival.festivalId);
  const currentStatus = festival.status || 'UNPUBLISHED';

  const fileList = [];
  if (festival.photoURL) {
    fileList.push({
      uid: '-1',
      name: festival.photo,
      url: festival.photoURL,
      status: 'done',
    });
  }

  if (showLoading && bridgeLoading) {
    return <Loading />;
  }

  const formFields = (
    <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
      <FormRow label="Festival Image">
        <FileUpload
          fullWidth={true}
          fileList={fileList}
          onFileUpload={onFileUpload}
          onFileDelete={onFileDelete}
          readOnly={readOnly}
        />
      </FormRow>
      <FormRow label="Event Name" error={errors.name?.message}>
        <input
          type="text"
          placeholder="Type an event name here..."
          {...register('name', { required: 'Name is required' })}
        />
      </FormRow>
      <DateFormWidget
        label="Event Dates & Times"
        dates={dates}
        handleDateChange={handleDateChange}
        allowMultiple={true}
        readOnly={readOnly}
        datesError={workspaceErrors.dates}
      />
      <FormRow
        label="Location"
        formField="location"
        error={workspaceErrors.location || workspaceErrors.locationName}
      >
        <LocationMapField
          location={festival.location}
          locationName={watchedLocationName}
          onLocationChange={readOnly ? undefined : handleLocationChange}
          onLocationNameChange={readOnly ? undefined : handleLocationNameChange}
          readOnly={readOnly}
        />
      </FormRow>
      <FormRow label="General Information" error={errors.description?.message}>
        <textarea
          placeholder="Festival Information..."
          {...register('description', {
            required: 'Description is required',
          })}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fas', 'globe']} />}>
        <input type="text" placeholder="Website" {...register('links.web')} />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'facebook']} />}>
        <input
          type="text"
          placeholder="Facebook"
          {...register('links.facebook')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'twitter']} />}>
        <input
          type="text"
          placeholder="Twitter"
          {...register('links.twitter')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'instagram']} />}>
        <input
          type="text"
          placeholder="Instagram"
          {...register('links.instagram')}
        />
      </FormRow>
    </fieldset>
  );

  return (
    <div>
      <FestivalHeader
        title={headerTitle}
        onSave={readOnly ? undefined : submitForm}
        showSave={!readOnly}
        showExit={true}
        editHref={
          readOnly && festival.festivalId
            ? `/festival/${festival.festivalId}`
            : undefined
        }
      />
      <div
        className={useSidebarLayout ? 'bethere-content-container' : 'w-full'}
      >
        <div className="event-main-info min-w-0">
          <form
            ref={formRef}
            className="admin-form"
            onSubmit={submitForm}
            noValidate
          >
            {formFields}
            {!readOnly && (
              <div className="mt-8 flex justify-end border-t border-black/[0.08] pt-6 pb-2">
                <Button
                  type="submit"
                  disabled={isSubmitting || saveMutation.isPending}
                  mode="dark"
                >
                  Save Festival
                </Button>
              </div>
            )}
          </form>
        </div>

        {useSidebarLayout && (
          <aside className="bethere-detail-sidebar">
            <FestivalEventStatusPanel
              festivalId={festival.festivalId}
              status={currentStatus}
              readOnly={readOnly}
              isPending={statusMutation.isPending}
              onStatusChange={handleStatusChange}
            />
            <FestivalQrCodesSidebar
              festivalId={festival.festivalId}
              status={currentStatus}
            />
          </aside>
        )}
      </div>
    </div>
  );
}

export { FestivalFormFields };

export function FestivalForm(props) {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const setFestival = useFestivalWorkspaceStore((s) => s.setFestival);
  const patchFestival = useFestivalWorkspaceStore((s) => s.patchFestival);
  const saveMutation = useSaveFestivalMutation();
  const statusMutation = useChangeFestivalStatusMutation();
  const currentUser = useAuthStore((s) => s.currentUser);
  const canModify = canModifyFestivals(currentUser);

  const id = props.match?.params?.id;
  const numericId =
    id !== undefined && id !== '' && !Number.isNaN(Number(id))
      ? String(id)
      : undefined;
  const isEditRoute =
    Boolean(numericId) &&
    props.match?.path != null &&
    !props.match.path.endsWith('/view');

  const readOnly =
    props.readOnly === true ||
    !canModify ||
    (props.match?.path != null && props.match.path.endsWith('/view'));

  const q = useFestivalDetailQuery(numericId);

  useEffect(() => {
    if (!props.match || !numericId) {
      setFestival({});
    }
  }, [props.match, numericId, setFestival]);

  useEffect(() => {
    if (q.isSuccess && q.data) {
      setFestival(q.data);
    }
  }, [q.isSuccess, q.data, setFestival]);

  if (isEditRoute && !canModify) {
    return <Redirect to={`/festival/${numericId}/view`} />;
  }

  const bridgeLoading = Boolean(numericId && q.isPending);
  const loading = saveMutation.isPending || statusMutation.isPending;

  return (
    <FestivalFormFields
      festival={festival}
      patchFestival={patchFestival}
      saveMutation={saveMutation}
      statusMutation={statusMutation}
      loading={loading}
      bridgeLoading={bridgeLoading}
      readOnly={readOnly}
      onNewFestivalSaved={props.onNewFestivalSaved}
    />
  );
}
